import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useWorkflowStatus } from '../contexts/WorkflowContext.jsx';
import UserMenu from './UserMenu.jsx';
import { LogoText, Icon, MobileBottomNav } from './ui/index.jsx';
import GlobalWorkflowIndicator from './GlobalWorkflowIndicator.jsx';
import FloatingFeedbackButton from './FloatingFeedbackButton.jsx';
import { createLogger } from '../utils/logger.js';

const log = createLogger('Navigation');

/**
 * Consistent Navigation Component for all pages
 * Handles both desktop and mobile navigation with proper authentication states
 */
export default function Navigation() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, authType, loading } = useAuth();
    const { isActive: workflowIsActive, isQueueMode } = useWorkflowStatus();


    const isAuthenticated = authType === 'authenticated';
    const currentPath = location.pathname;

    // Navigate to signin while preserving referral code from URL
    const navigateToSignIn = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const ref = urlParams.get('ref');
        log.log('Current URL search:', window.location.search);
        log.log('Ref param found:', ref);
        const targetUrl = ref ? `/signin?ref=${encodeURIComponent(ref)}` : '/signin';
        log.log('Navigating to:', targetUrl);
        navigate(targetUrl);
    };

    const isActive = (path) => {
        if (path === '/' && currentPath === '/') return true;
        if (path !== '/' && currentPath.startsWith(path)) return true;
        return false;
    };

    const getLinkClass = (path) => {
        return isActive(path)
            ? "text-white"
            : "text-[#CCCCCC] hover:text-white transition-colors";
    };



    const bottomNavItems = [
        { id: 'home', label: 'Home', icon: 'home', onClick: () => navigate('/'), path: '/' },
        { id: 'how-it-works', label: 'How It Works', icon: 'question', onClick: () => navigate('/how-it-works'), path: '/how-it-works' },
        { id: 'plans', label: 'Plans', icon: 'creditCard', onClick: () => navigate('/plans'), path: '/plans' },
        { id: 'about', label: 'About', icon: 'document', onClick: () => navigate('/about'), path: '/about' },
        { id: 'contact', label: 'Contact', icon: 'mail', onClick: () => navigate('/contact'), path: '/contact' }
    ];

    return (
        <>
            <header className="bg-black/50 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50 transition-all duration-300">
                {/* Top bar for workflow/queue indicator - only shows when active */}
                {(workflowIsActive || isQueueMode) && (
                    <div className="w-full flex justify-center py-2 border-b border-white/5 bg-black/30">
                        <GlobalWorkflowIndicator inline={true} />
                    </div>
                )}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center py-4 relative">
                        {/* Logo - Left */}
                        <div className="flex items-center">
                            <button
                                onClick={() => navigate('/')}
                                className="flex items-center hover:opacity-80 transition-opacity"
                            >
                                <LogoText size="lg" />
                            </button>
                            {/* Mobile quick Sign In next to logo - hide while loading */}
                            {!loading && !user && (
                                <button
                                    onClick={navigateToSignIn}
                                    className="md:hidden ml-3 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#7B61FF]/10 border border-[#7B61FF]/40 text-[#BA87FF] hover:bg-[#7B61FF]/20 transition-colors"
                                >
                                    Sign In
                                </button>
                            )}
                        </div>

                        {/* Desktop Navigation - Center */}
                        <nav className="hidden md:flex items-center space-x-6 absolute left-1/2 transform -translate-x-1/2">
                            <button
                                onClick={() => navigate('/')}
                                className={getLinkClass('/')}
                            >
                                Home
                            </button>

                            <button
                                onClick={() => navigate('/how-it-works')}
                                className={getLinkClass('/how-it-works')}
                            >
                                How It Works
                            </button>

                            <button
                                onClick={() => navigate('/plans')}
                                className={getLinkClass('/plans')}
                            >
                                Plans
                            </button>

                            <button
                                onClick={() => navigate('/about')}
                                className={getLinkClass('/about')}
                            >
                                About
                            </button>

                            <button
                                onClick={() => navigate('/contact')}
                                className={getLinkClass('/contact')}
                            >
                                Contact Us
                            </button>

                            {/* Show Dashboard link for authenticated users */}
                            {isAuthenticated && (
                                <button
                                    onClick={() => navigate('/dashboard')}
                                    className={getLinkClass('/dashboard')}
                                >
                                    Dashboard
                                </button>
                            )}
                        </nav>

                        {/* Right side - Auth */}
                        <div className="flex items-center space-x-4">
                            {/* Desktop - User Menu or Sign In (hide Sign In while loading) */}
                            {user ? (
                                <div className="hidden md:block">
                                    <UserMenu />
                                </div>
                            ) : !loading ? (
                                <div className="hidden md:flex items-center space-x-4">
                                    <button
                                        onClick={navigateToSignIn}
                                        className="text-gray-300 hover:text-white transition-colors"
                                    >
                                        Sign In
                                    </button>
                                </div>
                            ) : null}
                            {/* Mobile User Menu (Avatar) - shown on right */}
                            {user && (
                                <div className="md:hidden">
                                    <UserMenu mobile={false} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Floating Feedback Button */}
                <FloatingFeedbackButton />
            </header>

            {/* Mobile Bottom Navigation */}
            <MobileBottomNav items={bottomNavItems} />
        </>
    );
}